import type { ReactNode } from 'react';
import classes from './AuthLayout.module.css';
import { Logo } from './Logo.js';

/** Arka plandaki deniz haritası eş yükselti çizgileri (süs). */
const CONTOUR_STEPS = [0, 1, 2, 3, 4, 5, 6, 7, 8] as const;

export interface AuthFeature {
  icon: ReactNode;
  title: string;
  description: string;
}

/** Şirket markası (ADR 0019 §4): eşleşmeden sonra marka panelinde Felix işaretinin yerine. */
export interface AuthBrand {
  name: string;
  tagline?: string | null | undefined;
  /** Logo görüntüsü (data URL); yoksa adın baş harfiyle monogram. */
  logo?: string | null | undefined;
}

/** Çerçevenin ortak alanları: form sütunu ve dar ekran logosu. */
interface AuthLayoutFrameProps {
  /** Verilirse sol üstte şirket markası; yoksa Felixmate işareti. */
  brand?: AuthBrand | null | undefined;
  /** Form alanının sağ üstü: dil seçimi gibi. */
  toolbar?: ReactNode;
  /** Formun üstünde şerit: güncelleme bildirimi gibi; formu örtmez (M6f G6-18). */
  notice?: ReactNode;
  /** Form kartının altındaki küçük not. */
  formFootnote?: ReactNode;
  /**
   * Dar ekranda (1024 px altı) formun altında gösterilen marka bandı: gizlenen sol panelin yerine
   * (tedarikçi portalının ikmal sahnesi; sahibin isteği 2026-10-23). Geniş ekranda gizlidir.
   */
  belowForm?: ReactNode;
  /**
   * Masaüstü uygulaması: düzen pencereye sabitlenir, yalnız form sütunu kayar, marka paneli yerinde
   * kalır (M6f G6-17). Web'de belge kayar (tam sayfa görüntüler).
   */
  fitWindow?: boolean;
  children: ReactNode;
}

/** Varsayılan marka paneli: başlık, maddeler ve deniz haritası süsü. */
interface AuthStandardPanelProps {
  /** Marka panelindeki başlık ve açıklama (çevrilmiş metin). */
  headline: string;
  subline: string;
  features: readonly AuthFeature[];
  /** Panelin dibindeki tek satır, ör. "Felixmate 0.1.0 · © 2026 Fluxesoft" (M6f G6-23). */
  footnote: string;
  /** Panelin sol altı, satırın üstünde (eski sürüm alanı; gemi uygulaması artık kullanmaz). */
  brandFooter?: ReactNode;
}

/**
 * Uygulamaya özel marka paneli: lacivert zemin ve dar ekranda gizlenme aynı kalır, içerik tümüyle
 * `panel`'den gelir (tedarikçi portalının ikmal sahnesi; sahibin isteği 2026-10-23).
 */
interface AuthCustomPanelProps {
  panel: ReactNode;
}

export type AuthLayoutProps = AuthLayoutFrameProps &
  (AuthStandardPanelProps | AuthCustomPanelProps);

function BrandMark({ brand }: { brand: AuthBrand }) {
  return (
    <span className={classes.brand}>
      {brand.logo ? (
        <img src={brand.logo} alt="" className={classes.brandLogo} />
      ) : (
        <span className={classes.monogram} aria-hidden>
          {brand.name.trim().charAt(0).toLocaleUpperCase()}
        </span>
      )}
      <span className={classes.brandText}>
        <span className={classes.brandName}>{brand.name}</span>
        {brand.tagline ? <span className={classes.brandTagline}>{brand.tagline}</span> : null}
      </span>
    </span>
  );
}

function StandardPanel({
  headline,
  subline,
  features,
  footnote,
  brand,
  brandFooter,
}: AuthStandardPanelProps & { brand?: AuthBrand | null | undefined }) {
  return (
    <>
      <svg
        className={classes.contours}
        viewBox="0 0 600 800"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        {CONTOUR_STEPS.map((step) => (
          <ellipse key={step} cx="470" cy="690" rx={90 + step * 62} ry={70 + step * 50} />
        ))}
        {/* Tek illüstrasyon: rota sağ kenardan gelir, eş yükselti halkalarının ortasındaki limanda
          biter; metnin altına girmez (G6-24) */}
        <path
          d="M660 588 C 612 630, 572 626, 536 652 S 490 674, 470 690"
          className={classes.route}
        />
        <circle cx="470" cy="690" r="11" className={classes.routeHalo} />
        <circle cx="470" cy="690" r="5" className={classes.routeDot} />
      </svg>
      <div className={classes.brandInner}>
        <div className={classes.brandTop}>
          {brand ? <BrandMark brand={brand} /> : <Logo inverted variant="full" size={56} />}
        </div>
        <div className={classes.copy}>
          <h1 className={classes.headline}>{headline}</h1>
          <p className={classes.subline}>{subline}</p>
        </div>
        <ul className={classes.features}>
          {features.map((feature) => (
            <li key={feature.title} className={classes.feature}>
              <span className={classes.featureIcon}>{feature.icon}</span>
              <span>
                <span className={classes.featureTitle}>{feature.title}</span>
                <span className={classes.featureText}>{feature.description}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className={classes.brandBottom}>
          {brandFooter ? <div className={classes.brandFooter}>{brandFooter}</div> : null}
          <p className={classes.footnote}>{footnote}</p>
        </div>
      </div>
    </>
  );
}

/**
 * Giriş ekranları düzeni: solda lacivert marka paneli, sağda form. Gemi uygulamasında pencere
 * yüksekliğine sabitlenir (`fitWindow`). Dar ekranda marka paneli gizlenir, logo formun üstüne gelir
 * (docs/ekranlar/README.md "Responsive kurallar"); `belowForm` verilmişse formun altında gösterilir.
 */
export function AuthLayout(props: AuthLayoutProps) {
  const { brand, toolbar, notice, formFootnote, belowForm, fitWindow = false, children } = props;
  return (
    <div className={classes.root} data-fit-window={fitWindow || undefined}>
      <aside className={classes.panelBrand}>
        {'panel' in props ? props.panel : <StandardPanel {...props} />}
      </aside>
      <main className={classes.panel}>
        <div className={classes.toolbar}>{toolbar}</div>
        <div className={classes.formWrap}>
          <div className={classes.mobileLogo}>
            {brand ? <BrandMark brand={brand} /> : <Logo size={34} />}
          </div>
          {notice ? <div className={classes.notice}>{notice}</div> : null}
          <div className={classes.card}>{children}</div>
          {formFootnote ? <div className={classes.formFootnote}>{formFootnote}</div> : null}
          {belowForm ? <div className={classes.belowForm}>{belowForm}</div> : null}
        </div>
      </main>
    </div>
  );
}
